import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { Button, Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFilePdf, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import useEmailLogViewer from './useEmailLogViewer';
import { Helpers } from '../../../utils/helpers';
import Container from '../../../components/ui/Container/Container';
import styles from './EmailLogViewer.module.css';

interface EmailLogViewerProps {
  emailLogId: string | undefined;
}

export const EmailLogViewer = ({ emailLogId }: EmailLogViewerProps) => {
  const manager = useEmailLogViewer(emailLogId);

  if (Helpers.isNullOrWhiteSpace(emailLogId)) {
    return <></>;
  }

  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getRecipientsByType = (type: number): string[] => {
    if (!manager.emailLog) return [];
    return manager.emailLog.recipients
      .filter((r) => r.type === type)
      .map((r) => r.email);
  };

  return (
    <Container visible={!Helpers.isNullOrWhiteSpace(emailLogId)}>
      <LoadingContainer isLoading={manager.loading}>
        {manager.emailLog && (
          <div className={styles.viewerContainer}>
            {/* Header with actions */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Email Details</h4>
              <div className="d-flex gap-2">
                <Button variant="secondary" size="sm" onClick={manager.backToSearch}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
                </Button>
                <Button variant="primary" size="sm" onClick={manager.downloadEml}>
                  <FontAwesomeIcon icon={faEnvelope} /> Download EML
                </Button>
                <Button variant="danger" size="sm" onClick={manager.downloadPdf}>
                  <FontAwesomeIcon icon={faFilePdf} /> Export PDF
                </Button>
              </div>
            </div>

            {/* Email metadata */}
            <Card className="mb-3">
              <Card.Body>
                <Row className="mb-2">
                  <Col md={2}>
                    <strong>From:</strong>
                  </Col>
                  <Col md={10}>{manager.emailLog.from}</Col>
                </Row>

                {getRecipientsByType(0).length > 0 && (
                  <Row className="mb-2">
                    <Col md={2}>
                      <strong>To:</strong>
                    </Col>
                    <Col md={10}>{getRecipientsByType(0).join(', ')}</Col>
                  </Row>
                )}

                {getRecipientsByType(1).length > 0 && (
                  <Row className="mb-2">
                    <Col md={2}>
                      <strong>Cc:</strong>
                    </Col>
                    <Col md={10}>{getRecipientsByType(1).join(', ')}</Col>
                  </Row>
                )}

                {getRecipientsByType(2).length > 0 && (
                  <Row className="mb-2">
                    <Col md={2}>
                      <strong>Bcc:</strong>
                    </Col>
                    <Col md={10}>{getRecipientsByType(2).join(', ')}</Col>
                  </Row>
                )}

                <Row className="mb-2">
                  <Col md={2}>
                    <strong>Subject:</strong>
                  </Col>
                  <Col md={10}>{manager.emailLog.subject}</Col>
                </Row>

                <Row className="mb-2">
                  <Col md={2}>
                    <strong>Sent Date:</strong>
                  </Col>
                  <Col md={10}>{formatDate(manager.emailLog.sendDateUtc)}</Col>
                </Row>

                {manager.emailLog.deliveredDateUtc && (
                  <Row className="mb-2">
                    <Col md={2}>
                      <strong>Delivered Date:</strong>
                    </Col>
                    <Col md={10}>{formatDate(manager.emailLog.deliveredDateUtc)}</Col>
                  </Row>
                )}

                <Row>
                  <Col md={2}>
                    <strong>Status:</strong>
                  </Col>
                  <Col md={10}>
                    <Badge
                      bg={
                        manager.emailLog.status === 'Delivered'
                          ? 'success'
                          : manager.emailLog.status === 'Sent'
                            ? 'primary'
                            : 'warning'
                      }
                    >
                      {manager.emailLog.status}
                    </Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Email body */}
            <Card>
              <Card.Header>
                <strong>Email Body</strong>
              </Card.Header>
              <Card.Body>
                <div
                  className={styles.emailBody}
                  dangerouslySetInnerHTML={{ __html: manager.emailLog.body }}
                />
              </Card.Body>
            </Card>
          </div>
        )}
        {!manager.emailLog && !manager.loading && (
          <div className="text-center text-muted py-4">
            <p>Email not found</p>
            <Button variant="secondary" onClick={manager.backToSearch}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
            </Button>
          </div>
        )}
      </LoadingContainer>
    </Container>
  );
};
